/**
 * Chat Application Hooks - Application Layer
 *
 * High-level chat operations that combine infrastructure APIs with business logic.
 * Components should use these hooks instead of calling infrastructure APIs directly.
 */

import { useState, useCallback } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { QueryClient } from "@tanstack/react-query";
import { useChatRoom } from "../../infrastructure/api/chatApi";

/**
 * High-level hook for managing a single chat room
 *
 * @example
 * ```tsx
 * const { room, isLoading } = useChatRoomOperations(chatId);
 * ```
 */
export function useChatRoomOperations(chatRoomId: string) {
  const query = useChatRoom(chatRoomId);

  return {
    room: query.room,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Chat room selection state management
 *
 * @example
 * ```tsx
 * const { selectedRoomId, selectRoom, clearSelection } = useChatRoomSelection();
 * ```
 */
export function useChatRoomSelection() {
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

  const selectRoom = useCallback((roomId: string) => {
    setSelectedRoomId(roomId);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRoomId(null);
  }, []);

  return {
    selectedRoomId,
    selectRoom,
    clearSelection,
  };
}
